import {
    Camera,
    KeyRound,
    Flame,
    Network,
    Cpu,
    BatteryCharging,
    ArrowUpRight,
} from "lucide-react";

const SERVICES = [
    {
        id: "service-cctv",
        code: "01",
        title: "CCTV",
        subtitle: "Videovigilancia",
        desc: "Cámaras de alta resolución, analítica con IA y monitoreo remoto en tiempo real.",
        bullets: [
            "Cámaras IP 4K / Térmicas",
            "Analítica con IA",
            "Grabación NVR redundante",
        ],
        img: "https://images.unsplash.com/photo-1618482914248-29272d021005?crop=entropy&cs=srgb&fm=jpg&w=1200",
        icon: Camera,
    },
    {
        id: "service-access",
        code: "02",
        title: "Control de Acceso",
        subtitle: "Identidad, Vehículos & Edificio",
        desc: "Sistemas biométricos, torniquetes peatonales, plumas de estacionamiento y automatización de elevadores.",
        bullets: [
            "Biometría · Facial · RFID",
            "Torniquetes peatonales · Plumas vehiculares",
            "Automatización de elevadores",
        ],
        img: "https://images.unsplash.com/photo-1697382608786-bcf4c113b86e?crop=entropy&cs=srgb&fm=jpg&w=1200",
        icon: KeyRound,
    },
    {
        id: "service-fire",
        code: "03",
        title: "Contra Incendios",
        subtitle: "Detección & Mitigación",
        desc: "Detección temprana, paneles certificados y supervisión 24/7 integrada.",
        bullets: [
            "Detectores foto-térmicos",
            "Paneles NFPA",
            "Notificación masiva",
        ],
        img: "https://images.unsplash.com/photo-1653353436174-a8e724190fd3?crop=entropy&cs=srgb&fm=jpg&w=1200",
        icon: Flame,
    },
    {
        id: "service-network",
        code: "04",
        title: "Voz & Datos",
        subtitle: "Conectividad, Soporte & Operación TI",
        desc: "Cableado estructurado, fibra óptica, telefonía IP y administración integral de tu infraestructura tecnológica.",
        bullets: [
            "Cableado estructurado · Fibra óptica",
            "Telefonía IP · Wi-Fi 6/7 empresarial",
            "Helpdesk 24/7 · Servidores · Ciberseguridad",
        ],
        img: "https://images.pexels.com/photos/37730212/pexels-photo-37730212.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=1000&w=1200",
        icon: Network,
    },
    {
        id: "service-domotics",
        code: "05",
        title: "Domótica",
        subtitle: "Automatización Inteligente",
        desc: "Integración y automatización de iluminación, climatización, persianas, audio y escenarios para edificios inteligentes.",
        bullets: [
            "Iluminación · Climatización · Persianas",
            "Audio multi-zona · Escenas y rutinas",
            "Integración KNX · Crestron · Control4",
        ],
        img: "https://images.unsplash.com/photo-1558002038-1055907df827?crop=entropy&cs=srgb&fm=jpg&w=1200",
        icon: Cpu,
    },
    {
        id: "service-energy",
        code: "06",
        title: "Energía",
        subtitle: "Potencia Continua & Energía Limpia",
        desc: "UPS, generadores y energía solar fotovoltaica para misión crítica y operación sustentable.",
        bullets: [
            "UPS / Banco de baterías",
            "Paneles solares · Sistemas híbridos",
            "Generadores & transferencia automática",
        ],
        img: "https://images.unsplash.com/photo-1509391366360-2e959784a276?crop=entropy&cs=srgb&fm=jpg&w=1200",
        icon: BatteryCharging,
    },
];

const Services = () => {
    return (
        <section
            id="servicios"
            data-testid="services-section"
            className="border-b border-white/10"
        >
            {/* Section header */}
            <div className="px-6 md:px-12 lg:px-16 py-16 md:py-24 grid grid-cols-12 gap-6 items-end border-b border-white/10">
                <div className="col-span-12 md:col-span-8">
                    <p className="text-xs md:text-sm font-mono tracking-[0.3em] uppercase text-white/50 mb-4">
                        / 002 — Servicios
                    </p>
                    <h2 className="font-display uppercase leading-[0.9] tracking-tighter text-5xl md:text-6xl lg:text-7xl">
                        Seis disciplinas.
                        <br />
                        <span className="text-stroke">Un solo socio.</span>
                    </h2>
                </div>
                <div className="col-span-12 md:col-span-4">
                    <p className="font-mono text-sm md:text-base text-white/60 leading-relaxed">
                        Integramos hardware, software y operación. Pasamos del
                        plano al sitio sin perder responsabilidad.
                    </p>
                </div>
            </div>

            {/* Bento grid */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3">
                {SERVICES.map((s, i) => (
                    <ServiceCard
                        key={s.id}
                        service={s}
                        index={i}
                        isLastRow={i >= SERVICES.length - 3}
                    />
                ))}
            </div>
        </section>
    );
};

const ServiceCard = ({ service, index }) => {
    const Icon = service.icon;
    return (
        <article
            data-testid={service.id}
            className="group relative min-h-[420px] border-r border-b border-white/10 overflow-hidden cursor-pointer bg-[#0a0a0a] transition-colors duration-200 hover:bg-[#1A9EC9]"
        >
            {/* Background image */}
            <div
                aria-hidden
                style={{ backgroundImage: `url(${service.img})` }}
                className="absolute inset-0 bg-cover bg-center opacity-25 group-hover:opacity-10 transition-opacity duration-300 grayscale group-hover:grayscale-0"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-black via-black/70 to-transparent group-hover:from-[#1A9EC9] group-hover:via-[#1A9EC9]/70 transition-colors" />

            <div className="relative h-full flex flex-col justify-between p-8 md:p-10">
                <div className="flex items-start justify-between">
                    <div className="flex items-center gap-4">
                        <span className="font-mono text-xs tracking-[0.3em] text-white/40 group-hover:text-black/60">
                            {service.code}
                        </span>
                        <Icon
                            size={22}
                            className="text-white group-hover:text-black"
                        />
                    </div>
                    <ArrowUpRight
                        size={22}
                        className="text-white/40 group-hover:text-black group-hover:rotate-45 transition-all duration-300"
                    />
                </div>

                <div>
                    <p className="font-mono text-[11px] tracking-[0.3em] uppercase text-white/50 group-hover:text-black/70 mb-3">
                        {service.subtitle}
                    </p>
                    <h3 className="font-display uppercase tracking-tighter text-3xl md:text-4xl lg:text-5xl text-white group-hover:text-black leading-none">
                        {service.title}
                    </h3>
                    <p className="mt-4 font-mono text-sm text-white/70 group-hover:text-black/80 leading-relaxed max-w-sm">
                        {service.desc}
                    </p>

                    <ul className="mt-6 space-y-1.5 opacity-0 group-hover:opacity-100 transition-opacity duration-300 max-h-0 group-hover:max-h-40 overflow-hidden">
                        {service.bullets.map((b) => (
                            <li
                                key={b}
                                className="font-mono text-[11px] tracking-[0.2em] uppercase text-black flex items-center gap-2"
                            >
                                <span className="w-1.5 h-1.5 bg-black inline-block" />
                                {b}
                            </li>
                        ))}
                    </ul>
                </div>
            </div>
        </article>
    );
};

export default Services;
